import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Address } from 'viem';

import { MARKETS, marketBySymbol } from './config.ts';
import { usePoll } from './hooks/usePoll.ts';
import { Feed, type FeedMessage, type FeedStatus } from './lib/feed.ts';
import { assetCtxs, hlEnabled, type AssetCtx } from './lib/hl.ts';
import { closedNet } from './lib/positions.ts';
import { pushTick } from './lib/ticks.ts';
import type { Signer } from './lib/intents.ts';
import {
  type Auction,
  type Buyback,
  type Converter,
  type Globals,
  type LivePosition,
  livePositions,
  type Market,
  parseSnapshot,
  parseWallet,
  type Queue,
  type Reserve,
  type UserState,
} from './lib/protocol.ts';
import type { PnlCardData } from './components/PnlCard.tsx';
import { parseAccount, parseTransfers, relayer, type Account, type ApiClosed, type IntentView, type Stats, type Transfer } from './lib/relayer.ts';
import { loadSession, type Session } from './lib/session.ts';
import { rememberWallet, restoreWallet, watchProvider, type Connected } from './lib/wallet.ts';

/**
 * `pnl` turns a toast into a realized-PnL card: net of fees, and its share of the margin in percent; `closed`, the
 * one position it is about, offers to share it
 */
export type Toast = {
  id: number;
  kind: 'ok' | 'err' | 'info';
  title: string;
  body?: string;
  href?: string;
  pnl?: { net: bigint; pct: number };
  closed?: ApiClosed;
};
export type Page = 'trade' | 'portfolio' | 'house' | 'leaderboard' | 'papurr' | 'stats' | 'docs';
/** `doc` is the docs page slug, e.g. `learn/liquidations` */
export type Route = { page: Page; market: number; doc?: string };
export type MidPoint = { t: number; px: bigint };

type App = {
  route: Route;
  /** `replace` moves without a history entry (the tour's page changes) */
  go: (r: Partial<Route>, opts?: { replace?: boolean }) => void;
  block?: bigint;
  globals?: Globals;
  markets?: Market[];
  /** Mids seen since the page loaded, per market, for the local chart and the session change */
  mids: Record<number, MidPoint[]>;
  /** 24h stats from Hyperliquid, mainnet only */
  ctxs?: Record<string, AssetCtx>;
  /** Open positions, traders and trading activity, from the API */
  stats?: Stats;
  /** HouseReserve holdings, when the deployment has one */
  reserve?: Reserve;
  /** HYPE buyback of the stakers' revenue, when the deployment has one */
  buyback?: Buyback;
  auction?: Auction;
  /** HYPE -> USDC conversions, when the deployment has a converter */
  converter?: Converter;
  /** Relayer queue as of the latest block */
  queue?: Queue;
  wallet?: Connected;
  setWallet: (w: Connected | undefined) => void;
  session?: Session;
  setSession: (s: Session | undefined) => void;
  signer?: Signer;
  user?: UserState;
  account?: Account;
  accountError?: string;
  positions?: LivePosition[];
  intents?: IntentView[];
  transfers?: Transfer[];
  /** WebSocket to the API: everything live comes through it */
  feed: FeedStatus;
  relayerDown: boolean;
  /** No new block has come through for a while: the price shown is old (chain, indexer or connection) */
  stale: boolean;
  funds?: 'deposit' | 'withdraw';
  openFunds: (f: 'deposit' | 'withdraw' | undefined) => void;
  connectOpen: boolean;
  openConnect: (v: boolean) => void;
  /** The quick tour card is up */
  tour: boolean;
  openTour: (v: boolean) => void;
  /** The shareable PnL card on screen */
  pnlCard?: PnlCardData;
  openPnlCard: (c: PnlCardData | undefined) => void;
  toast: (t: Omit<Toast, 'id'>) => void;
  toasts: Toast[];
  dismiss: (id: number) => void;
  /** Asks the server to push this user's data again (it also pushes on every change by itself) */
  refresh: () => void;
};

const Ctx = createContext<App | null>(null);

export function useApp(): App {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside <AppProvider>');
  return v;
}

const MID_POINTS = 1_200;
/** Blocks come about once a second; this long without one and the data on screen is old */
const STALE_MS = 20_000;
const PAGES: Page[] = ['trade', 'portfolio', 'house', 'leaderboard', 'papurr', 'stats', 'docs'];

function parseHash(): Route {
  const [page, ...rest] = window.location.hash.replace(/^#\/?/, '').split('/');
  const p = PAGES.includes(page as Page) ? (page as Page) : 'trade';
  if (p === 'docs') return { page: p, market: 0, doc: rest.filter(Boolean).join('/') || undefined };
  return { page: p, market: rest[0] ? marketBySymbol(rest[0]) : 0 };
}

function toHash(r: Route): string {
  if (r.page === 'docs') return r.doc ? `#/docs/${r.doc}` : '#/docs';
  return r.page === 'trade' ? `#/trade/${(MARKETS[r.market]?.symbol ?? 'btc').toLowerCase()}` : `#/${r.page}`;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>(parseHash);
  const [wallet, setWalletState] = useState<Connected>();
  const [session, setSession] = useState<Session>();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [mids, setMids] = useState<Record<number, MidPoint[]>>({});
  const [funds, openFunds] = useState<'deposit' | 'withdraw'>();
  const [connectOpen, openConnect] = useState(false);
  const [tour, openTour] = useState(false);
  const [pnlCard, openPnlCard] = useState<PnlCardData>();
  const nextToast = useRef(1);
  const user: Address | undefined = wallet?.address;

  useEffect(() => {
    const on = () => setRoute(parseHash());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  const go = useCallback((r: Partial<Route>, opts?: { replace?: boolean }) => {
    setRoute((cur) => {
      const next = { ...cur, ...r };
      if (window.location.hash !== toHash(next)) {
        if (opts?.replace) window.history.replaceState(null, '', toHash(next));
        else window.history.pushState(null, '', toHash(next));
      }
      return next;
    });
  }, []);

  const setWallet = useCallback((w: Connected | undefined) => {
    rememberWallet(w);
    setWalletState(w);
  }, []);

  const toast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = nextToast.current++;
    setToasts((xs) => [...xs.slice(-3), { ...t, id }]);
    setTimeout(() => setToasts((xs) => xs.filter((x) => x.id !== id)), t.pnl ? 6_000 : t.kind === 'err' ? 12_000 : 5_000);
  }, []);
  const dismiss = useCallback((id: number) => setToasts((xs) => xs.filter((x) => x.id !== id)), []);

  // live state, all pushed by the API: no RPC reads from the browser
  const [snapshot, setSnapshot] = useState<{
    block: bigint;
    globals: Globals;
    markets: Market[];
    queue?: Queue;
    reserve?: Reserve;
    buyback?: Buyback;
    auction?: Auction;
    converter?: Converter;
  }>();
  const [userState, setUserState] = useState<UserState>();
  const [account, setAccount] = useState<Account>();
  const [intents, setIntents] = useState<IntentView[]>();
  const [transfers, setTransfers] = useState<Transfer[]>();
  const [feedStatus, setFeedStatus] = useState<FeedStatus>('connecting');
  const [downSince, setDownSince] = useState<number>();
  const feed = useRef<Feed>(undefined);
  const userRef = useRef<Address | undefined>(undefined);
  // the server greets every connection with its last snapshot, so a live socket does not prove that data
  // flows: a block number that keeps advancing does. Chain, indexer or socket, the symptom is the same.
  const lastBlock = useRef<bigint>(undefined);
  const [lastBlockAt, setLastBlockAt] = useState<number>();

  useEffect(() => {
    const onMessage = (m: FeedMessage) => {
      // user frames for a previous subscription can still be in flight after a switch: the user named on the
      // frame must be the current one
      const mine = !m.user || m.user.toLowerCase() === userRef.current?.toLowerCase();
      switch (m.type) {
        case 'market': {
          const snap = parseSnapshot(m.data);
          setSnapshot(snap);
          const t = Date.now();
          if (lastBlock.current === undefined || snap.block > lastBlock.current) {
            lastBlock.current = snap.block;
            setLastBlockAt(t);
          }
          // on a local chain the pushed mid is the only live price; mainnet ticks come from Hyperliquid's bbo
          if (!hlEnabled) {
            for (const mk of snap.markets) if (mk.mid !== undefined) pushTick(mk.id, { t, px: Number(mk.mid) / mk.inst.priceScale });
          }
          setMids((h) => {
            const next = { ...h };
            for (const mk of snap.markets) {
              if (mk.mid === undefined) continue;
              next[mk.id] = [...(next[mk.id] ?? []), { t, px: mk.mid }].slice(-MID_POINTS);
            }
            return next;
          });
          break;
        }
        // user frames for a previous subscription can still be in flight after a switch
        case 'wallet':
          if (userRef.current && mine) setUserState(parseWallet(m.data));
          break;
        case 'account':
          if (userRef.current && mine) setAccount(parseAccount(m.data));
          break;
        case 'intents':
          if (userRef.current && mine) setIntents(m.data as IntentView[]);
          break;
        case 'transfers':
          if (userRef.current && mine) setTransfers(parseTransfers(m.data));
          break;
      }
    };
    const onStatus = (st: FeedStatus) => {
      setFeedStatus(st);
      setDownSince((cur) => (st === 'open' ? undefined : (cur ?? Date.now())));
    };
    feed.current = new Feed(onMessage, onStatus);
    return () => feed.current?.close();
  }, []);

  useEffect(() => {
    userRef.current = user;
    setUserState(undefined);
    setAccount(undefined);
    setIntents(undefined);
    setTransfers(undefined);
    feed.current?.subscribe(user);
  }, [user]);

  // positions that reach the history while the app is open get a realized-PnL toast, whoever closed them
  // (the user, a limit or a liquidation); the first account frame only seeds what was already closed
  const seenClosed = useRef<Set<bigint>>(undefined);
  const globalsRef = useRef<Globals>(undefined);
  globalsRef.current = snapshot?.globals;
  useEffect(() => {
    seenClosed.current = undefined;
  }, [user]);
  useEffect(() => {
    if (!account) return;
    const seen = seenClosed.current;
    if (!seen) {
      seenClosed.current = new Set(account.history.map((h) => h.id));
      return;
    }
    const fresh = account.history.filter((h) => !seen.has(h.id));
    if (fresh.length === 0) return;
    for (const h of fresh) seen.add(h.id);
    const net = fresh.reduce((a, h) => a + closedNet(h, globalsRef.current), 0n);
    const margin = fresh.reduce((a, h) => a + h.margin, 0n);
    const pct = margin > 0n ? Number((net * 10_000n) / margin) / 100 : 0;
    const h = fresh[0]!;
    const title =
      fresh.length > 1
        ? `${fresh.length} positions closed`
        : `${MARKETS[h.instrumentId]?.symbol ?? 'Position'} ${h.isLong ? 'long' : 'short'} ${h.leverage}× ${h.outcome === 'liquidated' ? 'liquidated' : 'closed'}`;
    toast({ kind: net < 0n ? 'err' : 'ok', title, pnl: { net, pct }, closed: fresh.length === 1 ? h : undefined });
  }, [account, toast]);

  // limit orders fill (or expire) while the user looks elsewhere: say so once, on the change of status
  const seenLimits = useRef<Map<string, string>>(undefined);
  useEffect(() => {
    seenLimits.current = undefined;
  }, [user]);
  useEffect(() => {
    if (!intents) return;
    const prev = seenLimits.current;
    seenLimits.current = new Map(intents.filter((i) => i.limit).map((i) => [i.id, i.status]));
    if (!prev) return;
    for (const i of intents) {
      const was = i.limit && prev.get(i.id);
      if (!i.limit || !was || was === i.status) continue;
      const symbol = MARKETS[i.limit.instrumentId]?.symbol ?? '';
      const tp = i.limit.kind === 'tp';
      const name = tp ? `Take profit on ${symbol} ${i.limit.isLong ? 'long' : 'short'}` : `Limit ${i.limit.isLong ? 'long' : 'short'} ${symbol} ${i.limit.leverage}×`;
      // a take profit that ran shows up as the close's realized-PnL toast
      if (i.status === 'succeeded') {
        if (!tp) toast({ kind: 'ok', title: `${name} filled` });
      }
      else if (i.status === 'failed') toast({ kind: 'err', title: `${name} failed`, body: i.error ?? undefined });
      else if (i.status === 'expired') toast({ kind: 'info', title: `${name} expired` });
    }
  }, [intents, toast]);

  // a few seconds of reconnecting is normal; longer, the UI says so. The clock below re-checks both
  // timers, so a silence is noticed even when nothing else re-renders.
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 5_000);
    return () => clearInterval(t);
  }, []);
  const relayerDown = downSince !== undefined && Date.now() - downSince > 4_000;
  const stale = lastBlockAt !== undefined && Date.now() - lastBlockAt > STALE_MS;

  const ctxs = usePoll(hlEnabled ? () => assetCtxs() : null, 30_000, []);
  const stats = usePoll(() => relayer.stats(), 10_000, []);
  const positions = useMemo(
    () => (account ? livePositions(account.positions, snapshot?.markets, snapshot?.globals) : undefined),
    [account, snapshot?.markets, snapshot?.globals],
  );

  useEffect(() => {
    restoreWallet().then((w) => w && setWalletState((cur) => cur ?? w), () => {});
  }, []);

  // restore a stored session key for this wallet
  useEffect(() => {
    setSession(undefined);
    if (!user) return;
    let alive = true;
    loadSession(user)
      .then((s) => alive && setSession(s))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user]);

  // follow account switches in the wallet
  useEffect(() => {
    if (!wallet?.provider) return;
    return watchProvider(wallet.provider, (a) => {
      if (!a || a !== wallet.address) setWallet(undefined);
    });
  }, [wallet, setWallet]);

  const refresh = useCallback(() => feed.current?.subscribe(userRef.current), []);

  const signer: Signer | undefined = session
    ? { kind: 'session', account: session.account }
    : wallet
      ? { kind: 'wallet', client: wallet.client }
      : undefined;

  const value = useMemo<App>(
    () => ({
      route,
      go,
      block: snapshot?.block,
      globals: snapshot?.globals,
      markets: snapshot?.markets,
      mids,
      ctxs: ctxs.data,
      stats: stats.data,
      queue: snapshot?.queue,
      reserve: snapshot?.reserve,
      buyback: snapshot?.buyback,
      auction: snapshot?.auction,
      converter: snapshot?.converter,
      wallet,
      setWallet,
      session,
      setSession,
      signer,
      user: userState,
      account,
      accountError: relayerDown ? 'connection to the server lost, reconnecting' : undefined,
      positions,
      intents,
      transfers,
      feed: feedStatus,
      relayerDown,
      stale,
      funds,
      openFunds,
      connectOpen,
      openConnect,
      tour,
      openTour,
      pnlCard,
      openPnlCard,
      toast,
      toasts,
      dismiss,
      refresh,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [route, snapshot, mids, ctxs.data, stats.data, wallet, session, userState, account, positions, intents, transfers, feedStatus, relayerDown, stale, funds, connectOpen, tour, pnlCard, toasts],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
